import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Bell,
  CalendarDays,
  Check,
  CheckCheck,
  ChevronRight,
  Circle,
  File,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderOpen,
  Grid2X2,
  ListTodo,
  Presentation,
  RefreshCw,
  Sparkles,
  Upload,
  Users,
} from "lucide-react";
import type {
  Notification,
  Page,
  PublicUser,
  Resource,
  Task,
  WorkspaceSummary,
} from "@sol-office/contracts";
import type { Occurrence } from "../calendar/types";
import { api, errorMessage, patch, RequestError } from "../api";
import {
  EmptyState,
  ErrorNotice,
  formatBytes,
  formatDate,
  Spinner,
  TextLink,
} from "../ui";
import {
  NotificationCard,
  safeNotificationHref,
  changedEvent,
  revokedEvent,
  signalNotificationChange,
} from "../notifications/shared";
import { taskDue } from "../tasks/shared";
import "./home.css";

type HomeData = {
  assignedTasks: Page<Task>;
  upcomingEvents: Page<Occurrence>;
  activity: Page<Notification>;
  recentFiles: Resource[];
  asOf: string;
  timeZone: string;
};
type Section = "assignedTasks" | "upcomingEvents" | "activity";
const taskStatuses = {
  todo: "To do",
  in_progress: "In progress",
  done: "Done",
};
export function HomePage({
  workspace,
  user,
  revision,
  go,
  navigate,
  onCreateTask,
  onInvite,
}: {
  workspace: WorkspaceSummary;
  user: PublicUser;
  revision: number;
  go: (view: string) => void;
  navigate: (href: string) => void;
  onCreateTask: () => void;
  onInvite?: () => void;
}) {
  const userName = user.name;
  const [data, setData] = useState<HomeData | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [busy, setBusy] = useState<Section | null>(null);
  const generation = useRef(0),
    activityRequest = useRef(0),
    controller = useRef<AbortController | null>(null),
    current = useRef(data);
  current.current = data;
  useEffect(
    () => () => {
      activityRequest.current++;
    },
    [workspace.id],
  );
  const load = useCallback(
    async (section?: Section) => {
      const previous = current.current;
      const id = ++generation.current;
      controller.current?.abort();
      const request = new AbortController();
      controller.current = request;
      if (section) setBusy(section);
      else if (!previous) setLoading(true);
      const query = new URLSearchParams({
        timeZone: user.timeZone,
        limit: "6",
      });
      if (section && previous) {
        const cursor = previous[section].nextCursor;
        if (!cursor) {
          setBusy(null);
          return;
        }
        query.set(
          {
            assignedTasks: "tasksCursor",
            upcomingEvents: "eventsCursor",
            activity: "activityCursor",
          }[section],
          cursor,
        );
        query.set("asOf", previous.asOf);
      }
      try {
        const result = await api<HomeData>(
          `/workspaces/${workspace.id}/home?${query}`,
          { signal: request.signal },
        );
        if (id !== generation.current) return;
        setData(
          section && previous
            ? {
                ...previous,
                [section]: {
                  items: [
                    ...new Map(
                      [
                        ...previous[section].items,
                        ...result[section].items,
                      ].map((item) => [
                        "eventId" in item
                          ? `${item.eventId}/${item.recurrenceId}`
                          : item.id,
                        item,
                      ]),
                    ).values(),
                  ],
                  nextCursor: result[section].nextCursor,
                },
              }
            : result,
        );
        setError("");
      } catch (e) {
        if (id === generation.current && !request.signal.aborted) {
          if (e instanceof RequestError && [401, 403, 404].includes(e.status))
            setData(null);
          setError(errorMessage(e));
        }
      } finally {
        if (id === generation.current) {
          setLoading(false);
          setBusy(null);
        }
      }
    },
    [workspace.id, user.timeZone],
  );
  useEffect(() => {
    void load();
    return () => {
      generation.current++;
      controller.current?.abort();
    };
  }, [load, revision]);
  useEffect(() => {
    const refresh = () => {
      void load();
    };
    const timer = setInterval(refresh, 30000);
    const revoked = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== workspace.id) return;
      generation.current++;
      activityRequest.current++;
      controller.current?.abort();
      setData(null);
      setLoading(false);
      setBusy(null);
      setError("Your access to this workspace is no longer available.");
    };
    window.addEventListener(changedEvent, refresh);
    window.addEventListener(revokedEvent, revoked);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener(changedEvent, refresh);
      window.removeEventListener(revokedEvent, revoked);
      window.removeEventListener("focus", refresh);
    };
  }, [load, workspace.id]);
  const more = (section: Section) =>
    data?.[section].nextCursor ? (
      <button
        className="button quiet home-more"
        disabled={busy !== null}
        onClick={() => void load(section)}
      >
        {busy === section ? "Loading…" : "Show more"}
      </button>
    ) : null;
  const openActivity = async (item: Notification) => {
    const request = ++activityRequest.current;
    const origin = location.href;
    try {
      const { notification } = await patch<{ notification: Notification }>(
        `/notifications/${item.id}`,
        { read: true },
      );
      if (request !== activityRequest.current || location.href !== origin)
        return;
      const href = safeNotificationHref(notification.href, workspace.id);
      signalNotificationChange();
      if (href) navigate(href);
    } catch (e) {
      if (request !== activityRequest.current || location.href !== origin)
        return;
      setError(errorMessage(e));
      void load();
    }
  };
  return (
    <>
      <div className="home-welcome">
        <div>
          <span className="eyebrow">
            {new Intl.DateTimeFormat("en", {
              weekday: "long",
              month: "long",
              day: "numeric",
            }).format(new Date())}
          </span>
          <h1>
            Welcome in, {userName.split(/\s+/)[0]}
            <span className="welcome-period">.</span>
          </h1>
          <p>Make a little space for your best work.</p>
        </div>
        <button className="button secondary" onClick={() => go("apps")}>
          <Grid2X2 size={16} />
          Explore your apps
          <ArrowUpRight size={15} />
        </button>
      </div>
      <section className="home-hero">
        <div className="hero-copy">
          <span className="hero-label">
            <span />
            YOUR IDEAS HAVE A HOME
          </span>
          <h2>
            Bring it all
            <br />
            <em>together.</em>
          </h2>
          <p>
            Files, plans, and the people who make
            <br className="desktop-only" /> them happen. Start something good.
          </p>
          <button className="button forest" onClick={() => go("files")}>
            Open your files
            <ArrowRight size={17} />
          </button>
        </div>
        <div className="hero-art" aria-hidden="true">
          <div className="hero-circle" />
          <div className="hero-grid" />
          <div className="hero-sheet sheet-behind">
            <div className="sheet-title" />
            <div className="sheet-bars">
              <i />
              <i />
              <i />
              <i />
              <i />
            </div>
            <span />
          </div>
          <div className="hero-sheet sheet-front">
            <div className="sheet-doc-icon">
              <FileText size={23} />
            </div>
            <div className="sheet-title" />
            <div className="sheet-line" />
            <div className="sheet-line short" />
            <div className="sheet-check-row">
              <span>
                <Check size={12} />
              </span>
              <i />
            </div>
            <div className="sheet-check-row">
              <span>
                <Check size={12} />
              </span>
              <i />
            </div>
          </div>
          <div className="floating-cursor">
            <span />
            <small>Ideas welcome</small>
          </div>
          <div className="hero-spark">
            <Sparkles size={25} strokeWidth={1.3} />
          </div>
        </div>
      </section>
      <div className="quick-actions">
        <button onClick={() => go("files")}>
          <span className="app-icon ochre">
            <Upload size={20} />
          </span>
          <div>
            <strong>Bring your files</strong>
            <small>Keep your work in one place</small>
          </div>
          <ArrowUpRight size={17} />
        </button>
        <button onClick={onCreateTask}>
          <span className="app-icon green">
            <ListTodo size={20} />
          </span>
          <div>
            <strong>Make a plan</strong>
            <small>Give your next step a name</small>
          </div>
          <ArrowUpRight size={17} />
        </button>
        <button onClick={onInvite ?? (() => go("members"))}>
          <span className="app-icon violet">
            <Users size={20} />
          </span>
          <div>
            <strong>{onInvite ? "Bring your team" : "Meet your team"}</strong>
            <small>Good things happen together</small>
          </div>
          <ArrowUpRight size={17} />
        </button>
      </div>
      {error && <ErrorNotice message={error} retry={() => void load()} />}
      <div className="home-section-caption">
        <span>Your work, in one place</span>
        <button
          className="text-link"
          onClick={() => void load()}
          disabled={loading || busy !== null}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>
      <div className="home-panels">
        <section className="panel" aria-label="Assigned tasks">
          <div className="panel-heading">
            <h2>Assigned to you</h2>
            <TextLink onClick={() => go("tasks?assignedTo=me")}>
              View tasks
            </TextLink>
          </div>
          {loading ? (
            <div className="panel-loading">
              <Spinner />
            </div>
          ) : data?.assignedTasks.items.length ? (
            <div className="recent-list">
              {data.assignedTasks.items.map((task) => (
                <button
                  className="home-task"
                  key={task.id}
                  onClick={() =>
                    navigate(`/w/${workspace.id}/tasks?task=${task.id}`)
                  }
                >
                  <Circle size={18} />
                  <span>
                    <strong>{task.title}</strong>
                    <small className={task.isOverdue ? "home-overdue" : ""}>
                      {taskStatuses[task.status]} ·{" "}
                      {taskDue(task, user.timeZone)}
                      {task.isOverdue ? " · Overdue" : ""}
                    </small>
                  </span>
                  <ChevronRight size={16} />
                </button>
              ))}
              {more("assignedTasks")}
            </div>
          ) : (
            <EmptyState
              icon={CheckCheck}
              title="No open tasks assigned to you."
              action={<TextLink onClick={onCreateTask}>Create a task</TextLink>}
            >
              Your next steps will appear here.
            </EmptyState>
          )}
        </section>
        <section className="panel" aria-label="Upcoming events">
          <div className="panel-heading">
            <h2>Coming up</h2>
            <TextLink onClick={() => go("calendar")}>Open calendar</TextLink>
          </div>
          <p className="home-panel-note">
            The next seven days · {user.timeZone}
          </p>
          {loading ? (
            <div className="panel-loading">
              <Spinner />
            </div>
          ) : data?.upcomingEvents.items.length ? (
            <div className="recent-list">
              {data.upcomingEvents.items.map((event) => (
                <button
                  className="home-task home-event"
                  key={`${event.eventId}/${event.recurrenceId}`}
                  onClick={() =>
                    navigate(
                      `/w/${workspace.id}/calendar${event.redacted ? "" : `?event=${event.eventId}&recurrenceId=${encodeURIComponent(event.recurrenceId)}`}`,
                    )
                  }
                >
                  <CalendarDays size={19} />
                  <span>
                    <strong>{event.redacted ? "Busy" : event.title}</strong>
                    <small>
                      {new Intl.DateTimeFormat(undefined, {
                        timeZone: event.allDay ? "UTC" : user.timeZone,
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        ...(event.allDay
                          ? {}
                          : { hour: "numeric", minute: "2-digit" }),
                      }).format(
                        new Date(
                          event.allDay
                            ? `${event.start.slice(0, 10)}T00:00:00Z`
                            : event.startAt,
                        ),
                      )}
                      {event.allDay ? " · All day" : ""}
                    </small>
                    {event.location && <small>{event.location}</small>}
                  </span>
                  <ChevronRight size={16} />
                </button>
              ))}
              {more("upcomingEvents")}
            </div>
          ) : (
            <EmptyState icon={CalendarDays} title="A little breathing room.">
              No events on your calendars in the next seven days.
            </EmptyState>
          )}
        </section>
        <section className="panel" aria-label="Recent activity">
          <div className="panel-heading">
            <h2>Activity for you</h2>
            <TextLink onClick={() => go("notifications")}>Open inbox</TextLink>
          </div>
          {loading ? (
            <div className="panel-loading">
              <Spinner />
            </div>
          ) : data?.activity.items.length ? (
            <div className="home-activity">
              {data.activity.items.map((item) => (
                <NotificationCard
                  key={item.id}
                  item={item}
                  onOpen={() => void openActivity(item)}
                />
              ))}
              {more("activity")}
            </div>
          ) : (
            <EmptyState icon={Bell} title="You’re up to date.">
              New conversations, assignments, and comments will appear here.
            </EmptyState>
          )}
        </section>
        <section className="panel" aria-label="Recently updated files">
          <div className="panel-heading">
            <h2>Recently updated files</h2>
            <TextLink onClick={() => go("files")}>View all</TextLink>
          </div>
          {loading ? (
            <div className="panel-loading">
              <Spinner />
            </div>
          ) : data?.recentFiles.length ? (
            <div className="recent-list">
              {data.recentFiles.map((item) => (
                <button
                  className="recent-file"
                  key={item.id}
                  onClick={() => go("files")}
                >
                  <ResourceIcon resource={item} />
                  <span>
                    <strong>{item.name}</strong>
                    <small>
                      {item.kind === "folder"
                        ? "Folder"
                        : formatBytes(item.sizeBytes)}{" "}
                      · {formatDate(item.updatedAt)}
                    </small>
                  </span>
                  <ChevronRight size={16} />
                </button>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={FolderOpen}
              title="A fresh space for your files."
              action={
                <TextLink onClick={() => go("files")}>
                  Add your first file
                </TextLink>
              }
            >
              Bring a document, a deck, or whatever you’re working on.
            </EmptyState>
          )}
        </section>
      </div>
    </>
  );
}

function ResourceIcon({ resource }: { resource: Resource }) {
  const Icon =
    resource.kind === "folder"
      ? Folder
      : resource.mimeType?.includes("spreadsheet")
        ? FileSpreadsheet
        : resource.mimeType?.includes("presentation")
          ? Presentation
          : FileText;
  return (
    <span
      className={`resource-icon ${resource.kind === "folder" ? "ochre" : "blue"}`}
    >
      <Icon size={21} strokeWidth={1.6} />
    </span>
  );
}
